import { createContext, useEffect, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { fetchProfileDetails } from "@redux/reducer.profile";
import { constants } from "@lib/constants";
import dayjs from "dayjs";

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const profileData = useSelector((s) => s.profileReducer.profileDetails);
  const { userId, accessToken: token } = useSelector((s) => s.authReducer);

  const [theme ,setTheme]= useState(profileData?.theme);
  const [saving, setSaving] = useState(false);

  useEffect(()=>{
    setTheme(profileData?.theme)
  },[profileData])

  // --------------------------------------------------
  // Apply theme to DOM + WebView
  // --------------------------------------------------
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
      root.style.colorScheme = "dark";
    } else {
      root.classList.remove("dark");
      root.style.colorScheme = "light";
    }

    if (window.androidTheme?.postMessage) {
      window.androidTheme.postMessage(
        JSON.stringify({ type: "THEME_CHANGED", theme })
      );
    }
  }, [theme,profileData]);

  // --------------------------------------------------
  // Toggle + Save FULL payload to API
  // --------------------------------------------------
  const toggleTheme = async () => {
    if (saving) return;

    const nextTheme = theme === "light" ? "dark" : "light";
    setSaving(true);

    try {
      setTheme(nextTheme)
      await axios.put(
        `${constants.BASE_URL}profile/saveSettings`,
        {
          userId,
          profileSection: 1,
          profileSetting: {
            firstName: profileData?.firstName || "",
            lastName: profileData?.lastName || "",
            dob: profileData?.ageRange?.dob
              ? dayjs(profileData.ageRange.dob).toISOString()
              : "",
            gender: profileData?.attributes?.gender || "",
            address: {
              fullAddress: profileData?.address?.fullAddress || "",
              city: profileData?.address?.city || "",
              pinCode: profileData?.address?.pinCode || "",
            },
            cinta_id: profileData?.cinta_id,
            theme: nextTheme, // ✅ only thing changing
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      dispatch(fetchProfileDetails({ params: userId }));
      toast.success(`Theme set to ${nextTheme}`);
    } catch (err) {
      toast.error("Failed to update theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark: theme === "dark",
        toggleTheme,
        saving,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
